import { applyCellFormatPatch } from '@atlas/core-utils/asset-sheet'
import type { AssetSheetColumnMapUpdateInput } from '@atlas/models'
import type { AssetSheetColumnMapEntryDTO, AssetSheetColumnMapTabDTO } from '@atlas/types'
import { AssetSheetColumnMapScope } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiErrorMessage } from '@/lib/api-error'
import { assetSheetColumnMapApi } from '@/services/asset-sheet-column-map.service'
import { assetSheetKey } from './useAssetSheet'

const columnMapKey = [...assetSheetKey, 'column-map'] as const
const activityKey = [...assetSheetKey, 'activity'] as const

/** Every tab's mapped columns. */
export const useAssetSheetColumnMap = () => useQuery({ queryKey: columnMapKey, queryFn: assetSheetColumnMapApi.list })

export interface ColumnUpdate {
	column: AssetSheetColumnMapEntryDTO
	input: AssetSheetColumnMapUpdateInput
	/** The ATLAS name of `input.valueCode`, so the change shows before the server answers. */
	valueLabel?: string
}

/** Whether `entry` is one of the entries a change to `column` with `scope` reaches. */
export const isTarget = (
	entry: AssetSheetColumnMapEntryDTO,
	column: AssetSheetColumnMapEntryDTO,
	scope: AssetSheetColumnMapScope | undefined,
) =>
	scope === AssetSheetColumnMapScope.ALL_TABS
		? entry.columnName === column.columnName && entry.axis === column.axis
		: entry.id === column.id

/** The column map as it will read once `update` is applied: what the page shows while the request is out. */
export function applyColumnUpdate(
	tabs: AssetSheetColumnMapTabDTO[],
	{ column, input, valueLabel }: ColumnUpdate,
): AssetSheetColumnMapTabDTO[] {
	const patch = (entry: AssetSheetColumnMapEntryDTO): AssetSheetColumnMapEntryDTO => ({
		...entry,
		...(input.columnName !== undefined && { columnName: input.columnName }),
		...(input.level !== undefined && { level: input.level }),
		...(input.cellFormat !== undefined && { cellFormat: applyCellFormatPatch(entry.cellFormat, input.cellFormat) }),
		...(input.valueCode !== undefined && {
			value: input.valueCode === null ? null : { code: input.valueCode, label: valueLabel ?? input.valueCode },
		}),
	})
	return tabs.map((t) => ({
		...t,
		columns: t.columns.map((c) => (isTarget(c, column, input.scope) ? patch(c) : c)),
	}))
}

/** Change a column. Shown at once on every tab it reaches, and put back as it was if the change is refused. */
export const useUpdateAssetSheetColumn = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ column, input }: ColumnUpdate) => assetSheetColumnMapApi.update(column.id, input),
		onMutate: async (update: ColumnUpdate) => {
			await queryClient.cancelQueries({ queryKey: columnMapKey })
			const previous = queryClient.getQueryData<AssetSheetColumnMapTabDTO[]>(columnMapKey)
			if (previous) queryClient.setQueryData(columnMapKey, applyColumnUpdate(previous, update))
			return { previous }
		},
		onError: (_error, _update, context) => {
			queryClient.setQueryData(columnMapKey, context?.previous)
		},
		onSettled: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: columnMapKey }),
				queryClient.invalidateQueries({ queryKey: activityKey }),
			])
		},
	})
}

/** A refusal split into the field it names and its reason, e.g. `columnName: "X" is already mapped on tab US`. */
export function columnEditErrorOf(error: unknown): { field?: string; message: string } {
	const message = apiErrorMessage(error, 'The column could not be changed.')
	const match = /^(?:Validation failed: )?([A-Za-z][\w.]*): (.+)$/s.exec(message)
	return match ? { field: match[1], message: match[2] } : { message }
}
